import type { BeneficiaryTypeGrantsSaveFailedAction } from '@Application/SubjectTypeDefinitions/Actions/beneficiaryTypeGrantsSaveFailed';
import SaveBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/Commands/SaveBeneficiaryTypeGrants';
import saveBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/saveBeneficiaryTypeGrants';
import SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import UsableBySection from '@UI/Pages/Administration/SubjectTypes/Shared/UsableBy';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type BeneficiaryTypeGrantsView from '@UI/Store/SubjectTypeDefinitions/Views/BeneficiaryTypeGrantsView';
import { css } from '@emotion/react';
import * as React from 'react';
import { ItemBreadcrumb } from '@verclam/breadcrumbs';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsStyle = css`
	display: flex;
	justify-content: flex-end;
`;

type Props = {
	readonly grants: BeneficiaryTypeGrantsView;
};

/**
 * The access draft of the beneficiary type. It starts from the loaded lines and is never re-seeded.
 * Saving is reversible and needs no confirmation: a line removed is visible before it is sent. A
 * refused save puts each field error back on its line; the toast shows the message.
 */
const BeneficiaryTypeAccessEditor: React.FC<Props> = ({ grants }) => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const [draft, setDraft] = React.useState(() => SubjectTypeGrantsFormData.fromGrants(grants.usableBy));
	const [isSaving, setIsSaving] = React.useState(false);

	const handleGrantsChange = React.useCallback(
		(update: (current: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData) => {
			setDraft(update);
		},
		[],
	);

	const handleSaveClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		setIsSaving(true);
		dispatch(saveBeneficiaryTypeGrants(new SaveBeneficiaryTypeGrants(accountId, draft.usableBy)))
			.catch((failure: BeneficiaryTypeGrantsSaveFailedAction) => {
				setDraft((current) => current.applyServerErrors(failure.payload?.fieldErrors ?? []));
			})
			.finally(() => setIsSaving(false));
	}, [accountId, dispatch, draft]);

	return (
		<React.Fragment>
			<ItemBreadcrumb label="Bénéficiaires" />
			<UsableBySection grants={draft} onChange={handleGrantsChange} isReadOnly={false} />
			<div css={actionsStyle}>
				<Button type="button" label="Enregistrer" loading={isSaving} onClick={handleSaveClick} />
			</div>
		</React.Fragment>
	);
};

export default BeneficiaryTypeAccessEditor;
